import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getPortfolioQuestion, PortfolioQuestionFilters, updatePortfolioAnalyticsDateFilter, updatePortfolioQuestionFilter } from '../store';
import styles from '../PortfolioAnalytics.module.scss';
import { DateFilter as DateFilterType, PreExecutionFilterOption } from '../../../documents/search/store';
import { Radio } from '../../../shared/radio/Radio';
import { getBaseEntityDropdownOptions } from '../../entity/store';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { formatDate, UTC_FORMAT } from '../../../../utils/luxon';
import { DropdownSummary } from '../../../shared/dropdown/DropdownSummary';
import { DatePicker } from '../../../shared/datepicker/DatePicker';
import { getAllDocumentNames } from '../../documents/store';
import { CaretDown, CaretSide } from '../../../shared/icons';
import { Icon } from '../../../shared/icon/Icon';
import { Scrollable } from '../../../shared/scrollable/Scrollable';

export const PortfolioAnalyticsFilter: React.FC = () => {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [exclusiveFilterWarningVisible, setExclusiveFilterWarningVisible] = useState(false);

    const dispatch = useAppDispatch();
    const portfolioQuestion = useAppSelector(getPortfolioQuestion);
    const entityOptions = useAppSelector(getBaseEntityDropdownOptions);
    const documentNames = useAppSelector(getAllDocumentNames);

    const updateDocumentFilter = useCallback((key: keyof PortfolioQuestionFilters, value: string[] | string | null | boolean | number[]) => dispatch(updatePortfolioQuestionFilter(key, value)), [dispatch]);
    const updateDateFilter = useCallback((key: keyof DateFilterType, value: string | null) => dispatch(updatePortfolioAnalyticsDateFilter(key, value)), [dispatch]);

    const { filters: { date, isExclusiveEntityFilter, entityIds, includePreExecution, documentNameIds }, parentDatasetId } = portfolioQuestion;

    const updateToDate = (value: Date | null) => updateDateFilter('to', isNull(value) ? value : formatDate(value, UTC_FORMAT));
    const updateFromDate = (value: Date | null) => updateDateFilter('from', isNull(value) ? value : formatDate(value, UTC_FORMAT));
    const toggleExclusiveFilter = () => {
        updateDocumentFilter('entityIds', entityIds?.slice(0, 2) || null);
        updateDocumentFilter('isExclusiveEntityFilter', !isExclusiveEntityFilter);
        setExclusiveFilterWarningVisible(false);
    };

    const documentNameOptions = useMemo(() => documentNames.filter(({ datasetId }) => datasetId === parentDatasetId).map(({ documentNameId, documentName }) => ({ value: documentNameId!.toString(), label: documentName })), [documentNames, parentDatasetId]);
    const documentNameDropdownOptions = useMemo(() => {
        let options: DropdownOption[] = documentNameOptions;
        if ((documentNameIds.length !== documentNameOptions.length) && documentNameOptions.length > 1) {
            options = [{ value: 'All', label: 'Select All', showDivider: true }, ...documentNameOptions];
        }
        return options;
    }, [documentNameOptions, documentNameIds]);
    const selectedDocumentNames = useMemo(() => documentNameOptions.filter(({ value }) => documentNameIds.includes(parseInt(value))), [documentNameOptions, documentNameIds]);

    const updateDocumentNameIds = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        const selectedOptions = dropdownValue as Options<DropdownOption>;
        let selected: number[] = [];
        if (selectedOptions.length > 0) {
            const includesAllSelected = selectedOptions.map(({ value }) => value).includes('All');
            if (includesAllSelected) {
                selected = documentNameDropdownOptions.filter(({ value }) => value !== 'All').map(({ value }) => parseInt(value));
            } else {
                selected = selectedOptions.map(({ value }) => parseInt(value));
            }
            dispatch(updatePortfolioQuestionFilter('documentNameIds', selected));
        }
    }, [dispatch, documentNameDropdownOptions]);

    const entityFilter = useMemo(() => {
        const getEntityName = (val: string) => entityOptions.find(({ value }) => value === val)!.label;
        if (isNull(entityIds) || isUndefined(entityIds)) {
            return null;
        }
        return entityIds.map(value => ({ value, label: getEntityName(value) }));
    }, [entityIds, entityOptions]);

    const preExecutionOptions: DropdownOption[] = useMemo(() => Object.values(PreExecutionFilterOption).map(value => ({ label: value, value })), []);

    const preExecutionFilter = useMemo(() => preExecutionOptions.find(({ value }) => value === includePreExecution) || null, [includePreExecution, preExecutionOptions]);

    const updatePreExecutionFilter = (option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option)) {
            value = (option as DropdownOption).value;
        }
        updateDocumentFilter('includePreExecution', value);
    };

    const updateEntitiesFilter = (option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option) && (option as Options<DropdownOption>).length) {
            value = (option as Options<DropdownOption>).map(({ value }) => value);
            if (isExclusiveEntityFilter && value.length > 2) {
                setExclusiveFilterWarningVisible(true);
                return;
            }
            setExclusiveFilterWarningVisible(false);
        }
        updateDocumentFilter('entityIds', value);
    };

    const fromDate = useMemo(() => (isUndefined(date) || isUndefined(date.from) || isNull(date.from)) ? null : new Date(date.from), [date]);
    const toDate = useMemo(() => (isUndefined(date) || isUndefined(date.to) || isNull(date.to)) ? null : new Date(date.to), [date]);
    const filtersOpenIcon = useMemo(() => filtersOpen ? CaretDown : CaretSide, [filtersOpen]);

    return (
        <div className={styles.filtersWrapper}>
            <Scrollable>
                <div className={styles.filtersHeader}>
                    Filters (Optional)
                    <div onClick={() => setFiltersOpen(!filtersOpen)} className={styles.filterCollapseIcon}>
                        <Icon icon={filtersOpenIcon} fontSize={16} />
                    </div>
                </div>
                {filtersOpen &&
                    <>
                        <div className={styles.filterWrapper}>
                            <div className={styles.filterLabel}>Document Types</div>
                            <div className={styles.dropdownWidth}>
                                <DropdownSummary
                                    value={selectedDocumentNames}
                                    options={documentNameDropdownOptions}
                                    onChange={updateDocumentNameIds}
                                    menuPortalTarget={document.body}
                                    isMulti={true}
                                    maxControlHeight='80px'
                                    isClearable={false}
                                />
                            </div>
                        </div>
                        <div className={styles.filterWrapper}>
                            <div className={styles.filterLabel}>
                                Parties
                                <div className={styles.exclusiveParties}>
                                    <Radio
                                        checked={isExclusiveEntityFilter || false}
                                        onChange={toggleExclusiveFilter}
                                        onClick={toggleExclusiveFilter}
                                        showWarning={exclusiveFilterWarningVisible}
                                        warning='Max 2 options for exclusive filter'
                                        label='Exclusive Entities'
                                        fontSize='12px'
                                        marginBottom='0px'
                                    />
                                </div>
                            </div>
                            <div className={styles.dropdownWidth}>
                                <DropdownSummary
                                    value={entityFilter}
                                    options={entityOptions}
                                    onChange={updateEntitiesFilter}
                                    menuPortalTarget={document.body}
                                    isMulti={true}
                                    maxControlHeight='80px'
                                    isClearable={true}
                                />
                            </div>
                        </div>
                        <div className={styles.filterWrapper}>
                            <div className={styles.filterLabel}>Include Pre-Execution Documents</div>
                            <div className={styles.dropdownWidth}>
                                <DropdownSummary
                                    value={preExecutionFilter}
                                    options={preExecutionOptions}
                                    onChange={updatePreExecutionFilter}
                                    menuPortalTarget={document.body}
                                    isMulti={false}
                                    maxControlHeight='80px'
                                    isClearable={false}
                                />
                            </div>
                        </div>
                        <div className={styles.dateWrapper}>
                            <div className={styles.filterLabel}>Executed Date</div>
                            <div className={styles.dateRangeWrapper}>
                                <div className={styles.dateRange}>
                                    <div className={styles.dateRangeLabel}>From:</div>
                                    <DatePicker
                                        onChange={updateFromDate}
                                        testId='date-from'
                                        value={fromDate}
                                        whiteBackground={true}
                                    />
                                </div>
                                <div className={styles.dateRange}>
                                    <div className={styles.dateRangeLabel}>To:</div>
                                    <DatePicker
                                        onChange={updateToDate}
                                        testId='date-to'
                                        value={toDate}
                                        whiteBackground={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }
            </Scrollable>
        </div>
    );
};
